import { useInView } from 'react-intersection-observer'

import { ComponentType, useCallback, useEffect, useState } from 'react'

interface UseLazyComponentOptions {
    threshold?: number
    rootMargin?: string
    triggerOnce?: boolean
}

export const useLazyImport = <T = ComponentType>(
    importFn: () => Promise<{ default: T }>,
    options: UseLazyComponentOptions = {}
) => {
    const { threshold = 0.1, rootMargin = '50px', triggerOnce = true } = options

    const [component, setComponent] = useState<T | null>(null)
    const [hasLoaded, setHasLoaded] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const { ref, inView } = useInView({
        threshold,
        rootMargin,
        triggerOnce,
    })

    const loadCompnent = useCallback(() => {
        setIsLoading(true)
        setHasLoaded(true)

        importFn()
            .then((module) => {
                setComponent(() => module.default)
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to load component:', error)
                setHasLoaded(false)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [importFn])

    const forceLoad = useCallback(() => {
        if (hasLoaded || isLoading) return

        loadCompnent()
    }, [loadCompnent, hasLoaded, isLoading])

    useEffect(() => {
        if (!inView || hasLoaded || isLoading) return

        loadCompnent()
    }, [inView, hasLoaded, isLoading, loadCompnent])

    return {
        ref,
        component,
        isLoading,
        hasLoaded,
        inView,
        forceLoad,
    }
}
